import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import Accordion from 'react-bootstrap/Accordion';
import FormInput from 'components/FormInput';
import { useNavigate, useParams } from 'react-router-dom';
import { onSubmitParams } from '../types';
import _cloneDeep from 'lodash/cloneDeep';
import AccordionItem from 'components/common/AccordionItem';
import SubtaskActions from './SubtaskActions';
import Modal from 'components/common/Modal/Modal';
import './Subtask.scss';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface SubProjectsFormValues {
  tasks: Array<TaskObject> | [];
  name: string;
}
interface TaskObject {
  name: string;
  subtasks: Array<SubtaskObject>;
}
interface SubtaskObject {
  name: string;
  tasks: Array<NestedtaskObject>;
}
interface NestedtaskObject {
  name: string;
}
interface SubProjectProps {
  isSingleEdit?: boolean;
  disabled?: boolean;
  onSubmitProject: (params: onSubmitParams) => void;
  projectDetails: Array<TaskObject>;
}

const Subtask = ({
  isSingleEdit = false,
  disabled,
  onSubmitProject,
  projectDetails,
}: SubProjectProps) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const { step, setupId: projectId } = useParams();
  const navigate = useNavigate();
  const [isTaskNew, setIsTaskNew] = useState(false);
  const [title, setTitle] = useState('');
  const [withIcon, setWithIcon] = useState(false);
  const [taskType, setTaskType] = useState({
    action: 'add',
    type: 'task',
    taskPos: 0,
    subtaskPos: 0,
    nestedTaskPos: 0,
  });
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const taskSchema = {
    name: '',
    subtasks: [],
  };

  const initialFormValues: SubProjectsFormValues = {
    tasks: [],
    name: '',
  };

  const formik = useFormik({
    initialValues: initialFormValues,
    onSubmit: async (values) => {},
  });

  const saveAndNext = () => {
    const values = {
      tasks: formik.values.tasks,
    };
    const params = {
      values: values,
      draft: true,
      redirectAfterSubmit: true,
    };
    onSubmitProject(params);
  };

  const handleTask = (
    action: string,
    type: string,
    taskPos: number = 0,
    subtaskPos: number = 0,
    nestedTaskPos: number = 0,
  ) => {
    if (type === 'task') {
      const arr = [...formik.values.tasks];
      if (action === 'add') {
        const isTaskExists = !!arr.find(
          ({ name }) => name === formik.values.name,
        );

        if (isTaskNew && !isTaskExists) {
          arr.push(taskSchema);
        } else {
          toast.error(
            t('error_task_already_exists', { name: formik.values.name }),
          );
        }
        setIsTaskNew(false);
        formik.resetForm();
        formik.setFieldValue('tasks', arr);
        if (!isTaskExists) {
          formik.setFieldValue(`tasks[${taskPos}].name`, formik.values.name);
        }
      } else if (action === 'delete') {
        arr.splice(taskPos, 1);
        formik.setFieldValue('tasks', arr);
      } else {
        formik.setFieldValue(`name`, arr[taskPos].name);
      }
    } else if (type === 'subtask') {
      const arr = _cloneDeep(formik.values.tasks);
      if (action === 'add') {
        const isTaskExists = !!arr[taskPos]?.subtasks?.find(
          ({ name }) => name === formik.values.name,
        );
        if (isTaskNew && !isTaskExists) {
          arr[taskPos].subtasks.push({
            name: '',
            tasks: [],
          });
        } else {
          toast.error(
            t('error_task_already_exists', { name: formik.values.name }),
          );
        }
        setIsTaskNew(false);
        formik.resetForm();
        formik.setFieldValue(`tasks`, arr);
        if (!isTaskExists) {
          formik.setFieldValue(
            `tasks[${taskPos}].subtasks[${subtaskPos}].name`,
            formik.values.name,
          );
        }
      } else if (action === 'delete') {
        arr[taskPos].subtasks.splice(subtaskPos, 1);
        formik.setFieldValue('tasks', arr);
      } else {
        formik.setFieldValue(`name`, arr[taskPos].subtasks[subtaskPos].name);
      }
    } else {
      const arr = _cloneDeep(formik.values.tasks);
      if (action === 'add') {
        const isTaskExists = !!arr[taskPos]?.subtasks?.[
          subtaskPos
        ]?.tasks?.find(({ name }) => name === formik.values.name);
        if (isTaskNew && !isTaskExists) {
          arr[taskPos].subtasks[subtaskPos].tasks.push({
            name: '',
          });
        } else {
          toast.error(
            t('error_task_already_exists', { name: formik.values.name }),
          );
        }
        setIsTaskNew(false);
        formik.resetForm();
        formik.setFieldValue(`tasks`, arr);
        if (!isTaskExists) {
          formik.setFieldValue(
            `tasks[${taskPos}].subtasks[${subtaskPos}].tasks[${nestedTaskPos}].name`,
            formik.values.name,
          );
        }
      } else if (action === 'delete') {
        arr[taskPos].subtasks[subtaskPos].tasks.splice(nestedTaskPos, 1);
        formik.setFieldValue('tasks', arr);
      } else {
        formik.setFieldValue(
          `name`,
          arr[taskPos].subtasks[subtaskPos].tasks[nestedTaskPos].name,
        );
      }
    }
  };

  useEffect(() => {
    if (projectDetails) {
      formik.setFieldValue('tasks', projectDetails);
    }
  }, [projectDetails]);

  return (
    <>
      <div className="sub-projects h-100 overflow-auto d-flex flex-column">
        <div className="step-wrapper flex-grow-1 overflow-auto">
          <div className="text-end mb-3">
            <Button
              variant="primary"
              hidden={disabled}
              onClick={() => {
                setTitle(t('modal_title_add_sub_project'));
                setTaskType({
                  action: 'add',
                  type: 'task',
                  taskPos: formik.values.tasks.length,
                  subtaskPos: 0,
                  nestedTaskPos: 0,
                });
                setWithIcon(false);
                setIsTaskNew(true);
                handleShow();
              }}
            >
              {t('modal_title_add_sub_project')}
            </Button>
          </div>
          {formik.values.tasks.length > 0 ? (
            <Accordion>
              {formik.values.tasks.map((task: TaskObject, index: number) => (
                <AccordionItem
                  key={`task-${index + 1}`}
                  eventKey={`task-${index + 1}`}
                  text={`${index + 1}. ${task.name}`}
                  className={`${!task.subtasks.length ? 'disabled' : ''}`}
                  rightAction={
                    <>
                      {task.subtasks.length > 0 && (
                        <small className="text-muted mb-0 mr-3">
                          ( {task?.subtasks.length} )
                        </small>
                      )}
                    </>
                  }
                  leftAction={
                    <SubtaskActions
                      disabled={disabled}
                      onAdd={() => {
                        setTitle(t('modal_title_add_sub_task'));
                        setTaskType({
                          action: 'add',
                          type: 'subtask',
                          taskPos: index,
                          subtaskPos:
                            formik.values.tasks[index].subtasks.length,
                          nestedTaskPos: 0,
                        });
                        setIsTaskNew(true);
                        setWithIcon(false);
                        handleShow();
                      }}
                      onDelete={() => {
                        setTaskType({
                          action: 'delete',
                          type: 'task',
                          taskPos: index,
                          subtaskPos:
                            formik.values.tasks[index].subtasks.length,
                          nestedTaskPos: 0,
                        });
                        setWithIcon(true);
                        handleShow();
                      }}
                      onEdit={() => {
                        setTaskType({
                          action: 'add',
                          type: 'task',
                          taskPos: index,
                          subtaskPos:
                            formik.values.tasks[index].subtasks.length,
                          nestedTaskPos: 0,
                        });
                        setTitle(t('modal_title_add_sub_task'));
                        handleTask(
                          'edit',
                          'task',
                          index,
                          formik.values.tasks[index].subtasks.length,
                          0,
                        );
                        setWithIcon(false);
                        handleShow();
                      }}
                    />
                  }
                >
                  {task?.subtasks.length > 0 &&
                    task.subtasks.map(
                      (subtask: SubtaskObject, subtaskIndex: number) => (
                        <Accordion
                          className={`sub-accordian ${
                            !subtask?.tasks?.length ? 'disabled' : ''
                          }`}
                        >
                          <AccordionItem
                            key={`subtask-${index}-${subtaskIndex + 1}`}
                            eventKey={`subtask-${index}-${subtaskIndex + 1}`}
                            text={` ${index + 1}.${subtaskIndex + 1} ${
                              subtask.name
                            }`}
                            noBorder
                            color="text-muted"
                            rightAction={
                              <>
                                {subtask?.tasks.length > 0 && (
                                  <small className="text-muted mb-0 mr-3 flex-grow-1">
                                    ( {subtask?.tasks.length} )
                                  </small>
                                )}
                              </>
                            }
                            leftAction={
                              <SubtaskActions
                                disabled={disabled}
                                onAdd={() => {
                                  setTaskType({
                                    action: 'add',
                                    type: '',
                                    taskPos: index,
                                    subtaskPos: subtaskIndex,
                                    nestedTaskPos:
                                      formik.values.tasks[index].subtasks[
                                        subtaskIndex
                                      ].tasks.length,
                                  });
                                  setWithIcon(false);
                                  setIsTaskNew(true);
                                  handleShow();
                                }}
                                onDelete={() => {
                                  setTaskType({
                                    action: 'delete',
                                    type: 'subtask',
                                    taskPos: index,
                                    subtaskPos: subtaskIndex,
                                    nestedTaskPos: 0,
                                  });
                                  setWithIcon(true);
                                  handleShow();
                                }}
                                onEdit={() => {
                                  setTaskType({
                                    action: 'add',
                                    type: 'subtask',
                                    taskPos: index,
                                    subtaskPos: subtaskIndex,
                                    nestedTaskPos: 0,
                                  });
                                  setTitle(t('modal_title_add_sub_task'));
                                  handleTask(
                                    'edit',
                                    'subtask',
                                    index,
                                    subtaskIndex,
                                    0,
                                  );
                                  setWithIcon(false);
                                  handleShow();
                                }}
                              />
                            }
                          >
                            <ol type="A" className="mb-0 p-0">
                              {subtask.tasks.map(
                                (nestedTask: any, nestedTaskIndex: number) => (
                                  <div className="end-task border-bottom d-flex">
                                    <li className="py-2 mb-0 text-muted me-3 nested-task">
                                      {nestedTask.name}
                                    </li>
                                    <SubtaskActions
                                      disabled={disabled}
                                      onDelete={() => {
                                        setTaskType({
                                          action: 'delete',
                                          type: '',
                                          taskPos: index,
                                          subtaskPos: subtaskIndex,
                                          nestedTaskPos: nestedTaskIndex,
                                        });
                                        setWithIcon(true);
                                        handleShow();
                                      }}
                                      onEdit={() => {
                                        setTaskType({
                                          action: 'add',
                                          type: '',
                                          taskPos: index,
                                          subtaskPos: subtaskIndex,
                                          nestedTaskPos: nestedTaskIndex,
                                        });
                                        handleTask(
                                          'edit',
                                          '',
                                          index,
                                          subtaskIndex,
                                          nestedTaskIndex,
                                        );
                                        handleShow();
                                      }}
                                    />
                                  </div>
                                ),
                              )}
                            </ol>
                          </AccordionItem>
                        </Accordion>
                      ),
                    )}
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="d-flex flex-column align-items-center w-100 h-100 text-center">
              <div className="text-center flex-grow-1 overflow-auto no-task-img" />
              <p className="font-bold mt-2 mb-0">
                {t('subproject_placeholder_text1')} <br />
                {t('subproject_placeholder_text2')}
              </p>
            </div>
          )}
        </div>
        <div className="d-flex justify-space-between">
          <Button
            variant="outline-primary"
            onClick={() => {
              isSingleEdit
                ? navigate(-1)
                : navigate({
                    pathname: `/project/setup/${projectId}/${Number(step) - 1}`,
                  });
            }}
          >
            {t('btn_back')}
          </Button>
          <Button
            hidden={disabled}
            variant="primary"
            className="ms-auto"
            onClick={saveAndNext}
          >
            {isSingleEdit ? t('btn_save-exit') : t('btn_save-next')}
          </Button>
        </div>
      </div>
      <Modal
        show={show}
        title={title}
        submitBtnText={withIcon ? t('btn_delete') : t('btn_add')}
        cancelBtnText={t('btn_goback')}
        withIcon={withIcon}
        footerContentAlignment={withIcon ? undefined : 'center'}
        onSubmit={() => {
          handleTask(
            taskType.action,
            taskType.type,
            taskType.taskPos,
            taskType.subtaskPos,
            taskType.nestedTaskPos,
          );
          handleClose();
        }}
        onClose={() => {
          handleClose();
          setIsTaskNew(false);
          formik.setFieldValue('name', '');
        }}
      >
        {taskType.action !== 'delete' && (
          <FormInput
            label={t('label_sub_project')}
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
          />
        )}
      </Modal>
    </>
  );
};

export default Subtask;
